import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Checkbox,
    FormControlLabel,
    TextField,
    Tooltip,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from '@mui/material';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Folder as FolderIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { useDocument } from '../../contexts/DocumentContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { Document } from '../../types/Document';
import { Category } from '../../types/Category';
import api from '../../services/api';

interface ExpandTreeResultsDialogProps {
    open: boolean;
    onClose: () => void;
    expansionResult: any;
    onApplyExpansion: () => Promise<void>;
    currentDocument?: Document;
    existingDocuments?: any[];
    onOpenDocument?: (document: any) => Promise<void>;
    refreshCurrentDocument?: () => Promise<void>;
    refreshDocuments?: () => Promise<void>;
    onNewDocument?: (name: string, open: boolean) => Promise<any>;
}

const ExpandTreeResultsDialog: React.FC<ExpandTreeResultsDialogProps> = ({
    open,
    onClose,
    expansionResult,
    onApplyExpansion,
    currentDocument,
    existingDocuments,
    onOpenDocument,
    refreshCurrentDocument,
    refreshDocuments,
    onNewDocument,
}) => {
    const [applying, setApplying] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const [subcaseName, setSubcaseName] = useState('Expanded');
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [processedTree, setProcessedTree] = useState<any>(null);
    const [existingCategories, setExistingCategories] = useState<Category[]>([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);
    const [createNewDocument, setCreateNewDocument] = useState(false);
    const [newDocumentName, setNewDocumentName] = useState('');
    const [expansionMode, setExpansionMode] = useState<'with-content' | 'content-in-category' | 'categories-only'>('with-content');

    // Generate default name for new document
    const generateDefaultName = (baseName: string): string => {
        if (!baseName) return 'New Document';

        // Check if the name already ends with "Copy" or "Copy(n)"
        const copyPattern = / Copy(?: \((\d+)\))?$/;
        const match = baseName.match(copyPattern);

        let baseWithoutCopy: string;
        let startIndex: number;

        if (match) {
            // Name already ends with "Copy" or "Copy(n)" - extract base and increment
            baseWithoutCopy = baseName.replace(copyPattern, '');
            startIndex = match[1] ? parseInt(match[1]) + 1 : 1;
        } else {
            // Name doesn't end with "Copy" - add "Copy (1)"
            baseWithoutCopy = baseName;
            startIndex = 1;
        }

        // Find the next available index by checking existing documents
        if (existingDocuments && existingDocuments.length > 0) {
            let index = startIndex;
            let suggestedName = `${baseWithoutCopy} Copy (${index})`;

            while (existingDocuments.some(doc => doc.name === suggestedName)) {
                index++;
                suggestedName = `${baseWithoutCopy} Copy (${index})`;
            }

            return suggestedName;
        } else {
            // No existing documents to check - return the first suggestion
            return `${baseWithoutCopy} Copy (${startIndex})`;
        }
    };

    // Handle checkbox change
    const handleCreateNewDocumentChange = (checked: boolean) => {
        setCreateNewDocument(checked);
        if (checked && currentDocument) {
            // Generate default name when checkbox is checked
            const baseName = currentDocument.name || 'Document';
            const defaultName = generateDefaultName(baseName);
            setNewDocumentName(defaultName);
        } else {
            // Clear name when checkbox is unchecked
            setNewDocumentName('');
        }
    };
    const { getTooltipTextForState } = useHelpContent();

    // Apply merging when expansionResult changes
    useEffect(() => {
        if (expansionResult && expansionResult.tree) {
            const mergeResult = mergeDuplicateCategories(expansionResult.tree);
            const validatedTree = validateAndFixTreeStructure(mergeResult.node);
            setMergingOccurred(mergeResult.merged);
            setProcessedTree(validatedTree);
        }
    }, [expansionResult]);

    // Fetch categories when dialog opens
    useEffect(() => {
        if (open && currentDocument) {
            fetchCategoriesForDocument();
        }
    }, [open, currentDocument]);

    const fetchCategoriesForDocument = async () => {
        if (!currentDocument) return;

        setFetchingCategories(true);
        try {
            const document = currentDocument;

            if (document && document.categoryId) {
                // Fetch the root category with all its children
                const rootCategory = await fetchCategoryWithChildren(document.categoryId);
                setExistingCategories([rootCategory]);
            } else {
                setExistingCategories([]);
            }
        } catch (error: any) {
            console.error('Error fetching categories for document:', error);
            setExistingCategories([]);
        } finally {
            setFetchingCategories(false);
        }
    };

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category (empty category)
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    // Helper function to check if a category is new (using the 'new' flag from the embedding service)
    const isNewCategory = (node: any): boolean => {
        if (!node || !node.name) return false;

        // Check if the embedding service has marked this category as new
        if (node.new === true) {
            return true;
        }

        // If no existing categories are loaded yet, we can't determine if it's new
        if (!existingCategories || existingCategories.length === 0) {
            return false;
        }

        // Check if it exists in the current structure
        const searchInExisting = (categories: Category[], targetName: string): boolean => {
            for (const category of categories) {
                if (category.name === targetName) {
                    return true;
                }
                if (category.children && category.children.length > 0) {
                    if (searchInExisting(category.children, targetName)) return true;
                }
            }
            return false;
        };

        return !searchInExisting(existingCategories, node.name);
    };

    // Simple approach: Only mark categories as new if they have the explicit 'new: true' flag
    const isNewCategorySimple = (node: any): boolean => {
        if (!node || !node.name) return false;

        // Only trust the explicit 'new: true' flag from the backend
        // Don't try to guess or infer
        return node.new === true;
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = (node: any): boolean => {
        if (!node) return false;
        if (node.name && /[\u0590-\u05FF\u0600-\u06FF]/.test(node.name)) return true;
        if (node.text && /[\u0590-\u05FF\u0600-\u06FF]/.test(node.text)) return true;
        return false;
    };

    // Helper function to validate and fix tree structure
    const validateAndFixTreeStructure = (node: any): any => {
        if (!node) return node;

        // If this is a content node, it should always be wrapped in a category.
        // This handles cases where the root itself is a content node, or a content node is directly under a category.
        if (node.type === 'content') {
            return node;
        }

        // If this is a category, validate its children
        if (node.type === 'category' && Array.isArray(node.children)) {
            // Recursively validate and fix children first
            const processedChildren = node.children.map((child: any) => validateAndFixTreeStructure(child));

            // Separate children into actual categories and categories that were created to hold content
            const actualCategoryChildren = processedChildren.filter((child: any) => child.type === 'category' && child.children.some((grandchild: any) => grandchild.type === 'category'));
            const contentHoldingCategories = processedChildren.filter((child: any) => child.type === 'category' && child.children.every((grandchild: any) => grandchild.type === 'content'));

            // If there are actual category children AND content-holding categories at this level,
            // consolidate the content-holding categories under a single new category.
            if (actualCategoryChildren.length > 0 && contentHoldingCategories.length > 0) {
                const allContentFromHoldingCategories: any[] = [];
                contentHoldingCategories.forEach((cat: any) => {
                    if (cat.children) {
                        allContentFromHoldingCategories.push(...cat.children);
                    }
                });

                const consolidatedContentCategory = {
                    type: 'category',
                    name: generateCategoryNameFromContent(allContentFromHoldingCategories), // Use helper for naming
                    children: allContentFromHoldingCategories
                };

                return {
                    ...node,
                    children: [
                        ...actualCategoryChildren,
                        consolidatedContentCategory
                    ]
                };
            }

            // If no consolidation needed, just return with processed children
            return {
                ...node,
                children: processedChildren
            };
        }

        return node;
    };

    // Helper function to merge duplicate categories
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || node.type !== 'category') {
            return { node, merged: false };
        }

        let merged = false;
        const processedChildren: any[] = [];
        const seenNames = new Set<string>();

        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                if (child.type === 'category') {
                    const processedChild = mergeDuplicateCategories(child);
                    if (processedChild.merged) merged = true;

                    if (seenNames.has(child.name)) {
                        // Merge with existing category
                        const existingChild = processedChildren.find(c => c.name === child.name);
                        if (existingChild && existingChild.children && child.children) {
                            existingChild.children.push(...child.children);
                            merged = true;
                        }
                    } else {
                        seenNames.add(child.name);
                        processedChildren.push(processedChild.node);
                    }
                } else {
                    processedChildren.push(child);
                }
            }
        }

        return {
            node: { ...node, children: processedChildren },
            merged
        };
    };

    // Helper function to fetch category with children
    const fetchCategoryWithChildren = async (categoryId: string): Promise<Category> => {
        try {
            // Use the correct endpoint: /api/categories?categoryId={id}
            const response = await api.get(`/categories?categoryId=${encodeURIComponent(categoryId)}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching category with children:', error);
            throw error;
        }
    };

    // Helper function to create or find the "Content of [Document Name]" category
    const getOrCreateContentCategory = async (parentId: string, documentName: string): Promise<string> => {
        // Always create the content category under the root (parentId)
        // Include the new document name if we're creating a new document
        let categoryName: string;
        if (createNewDocument && newDocumentName.trim()) {
            categoryName = `Content of ${newDocumentName.trim()}`;
        } else {
            categoryName = `Content of ${documentName}`;
        }
        
        // First try to find existing category under the root
        const existingCategory = await findExistingCategory(parentId, categoryName);
        if (existingCategory) {
            return existingCategory;
        }
        
        // Create new category if it doesn't exist
        const newCategoryResponse = await api.post('/categories', {
            name: categoryName,
            parentId: parentId,
            description: "Content items from tree expansion"
        });
        
        return newCategoryResponse.data.id;
    };

    // Helper function to create tree structure
    // Process the expansion tree by adding content to existing categories and creating new ones
    const processExpansionTree = async (node: any, parentId: string, isRoot: boolean = false, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        // Skip processing the root category - it already exists
        if (isRoot && node.type === 'category') {
            if (Array.isArray(node.children)) {
                for (const child of node.children) {
                    await processExpansionTree(child, parentId, false, mode, rootCategoryId); // Pass rootId as last parameter
                }
            }
            return parentId;
        }

        // Additional check: if this looks like a root category duplicate, skip it
        if (node.type === 'category' && !node.new && node.name === existingCategories[0]?.name) {
            if (Array.isArray(node.children)) {
                for (const child of node.children) {
                    await processExpansionTree(child, parentId, false, mode, rootCategoryId); // Pass rootId as last parameter
                }
            }
            return parentId;
        }

        if (node.type === 'category') {
            return await processExpansionCategory(node, parentId, mode, rootCategoryId); // Pass rootId as last parameter
        } else if (node.type === 'content') {
            return await processExpansionContent(node, parentId, mode, rootCategoryId); // Pass rootId as last parameter
        }
        return null;
    };

    // Helper function to collect all content nodes from the tree
    const collectAllContentNodes = (node: any): any[] => {
        let contentNodes: any[] = [];
        
        if (node.type === 'content') {
            contentNodes.push(node);
        }
        
        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                contentNodes = contentNodes.concat(collectAllContentNodes(child));
            }
        }
        
        return contentNodes;
    };

    // Helper function to process all content nodes into the content category
    const processAllContentIntoCategory = async (contentNodes: any[], contentCategoryId: string) => {
        for (const contentNode of contentNodes) {
            if (contentNode.text && contentNode.type === 'content') {
                await api.post('/content', {
                    text: contentNode.text,
                    subcase: subcaseName,
                    parentId: contentCategoryId
                });
            } else if (contentNode.index !== undefined && expansionResult?.originalContents) {
                const originalContent = expansionResult.originalContents[contentNode.index];
                if (originalContent) {
                    await api.post('/content', {
                        text: originalContent.text,
                        subcase: originalContent.subcase || subcaseName,
                        parentId: contentCategoryId
                    });
                }
            }
        }
    };

    // Process category nodes in expansion tree
    const processExpansionCategory = async (node: any, parentId: string, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        let categoryId: string;

        if (node.new) {
            // This is a new category - create it
            const newCategoryResponse = await api.post('/categories', {
                name: node.name,
                parentId: parentId,
                description: ""
            });
            categoryId = newCategoryResponse.data.id;
        } else {
            // This is an existing category - find it
            categoryId = await findExistingCategory(parentId, node.name);
            if (!categoryId) {
                const newCategoryResponse = await api.post('/categories', {
                    name: node.name,
                    parentId: parentId,
                    description: ""
                });
                categoryId = newCategoryResponse.data.id;
            }
        }

        // Process children (content and subcategories)
        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                // In content-in-category mode, only process category children, skip content
                if (mode === 'content-in-category' && child.type === 'content') {
                    continue; // Skip content processing, we'll handle it separately
                }
                await processExpansionTree(child, categoryId, false, mode, rootCategoryId);
            }
        }

        return categoryId;
    };

    // Process content nodes in expansion tree
    const processExpansionContent = async (node: any, parentId: string, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        // Handle different expansion modes
        if (mode === 'categories-only') {
            // Skip content creation - only create categories
            return;
        }

        let targetParentId = parentId;
        
        // If mode is 'content-in-category', redirect content to the special category under the root
        if (mode === 'content-in-category' && currentDocument) {
            try {
                // Use the rootCategoryId that was passed through the tree processing chain
                targetParentId = await getOrCreateContentCategory(rootCategoryId, currentDocument.name);
            } catch (error) {
                console.error('Error creating content category:', error);
                // Fallback to original parent
                targetParentId = parentId;
            }
        }

        // Handle content nodes with text directly
        if (node.text && node.type === 'content') {
            await api.post('/content', {
                text: node.text,
                subcase: subcaseName,
                parentId: targetParentId
            });
            return;
        }

        // Handle content nodes with index reference (fallback)
        if (node.index !== undefined && expansionResult?.originalContents) {
            const originalContent = expansionResult.originalContents[node.index];
            if (originalContent) {
                await api.post('/content', {
                    text: originalContent.text,
                    subcase: originalContent.subcase || subcaseName,
                    parentId: targetParentId
                });
            }
        }
    };



    // Helper function to generate category name from content
    const generateCategoryNameFromContent = (contentNodes: any[]): string => {
        if (!contentNodes || contentNodes.length === 0) return 'New Category';

        // Try to extract a meaningful name from the first content item
        const firstContent = contentNodes[0];
        if (firstContent.text) {
            const text = firstContent.text.substring(0, 30);
            return text.length < 30 ? text : text + '...';
        }

        return `Content Category (${contentNodes.length} items)`;
    };

    // Helper function to find existing category
    const findExistingCategory = (parentId: string, categoryName: string): any => {
        const searchInCategories = (categories: Category[], targetParentId: string, targetName: string): any => {
            for (const category of categories) {
                if (category.parentId === targetParentId && category.name === targetName) {
                    return category.id;
                }
                if (category.children && category.children.length > 0) {
                    const found = searchInCategories(category.children, targetParentId, targetName);
                    if (found) return found;
                }
            }
            return null;
        };

        return searchInCategories(existingCategories, parentId, categoryName);
    };

    // Handle apply expansion
    const handleApplyExpansion = async () => {
        if (!processedTree) return;

        setApplying(true);
        try {
            if (createNewDocument) {
                // Create new document with expanded tree
                if (!newDocumentName.trim()) {
                    alert('Please enter a document name');
                    return;
                }
                if (!onNewDocument) {
                    alert('Create document function not available');
                    return;
                }

                // Create the new document
                const newDocument = await onNewDocument(newDocumentName.trim(), false);

                // Process the expansion tree in the new document
                if (newDocument.categoryId) {
                    if (expansionMode === 'content-in-category') {
                        // First, create all categories without content
                        await processExpansionTree(processedTree, newDocument.categoryId, true, 'categories-only', newDocument.categoryId);
                        
                        // Then collect all content nodes and process them into the content category
                        const allContentNodes = collectAllContentNodes(processedTree);
                        if (allContentNodes.length > 0) {
                            const contentCategoryId = await getOrCreateContentCategory(newDocument.categoryId, newDocument.name);
                            await processAllContentIntoCategory(allContentNodes, contentCategoryId);
                        }
                    } else {
                        // Normal processing for other modes
                        await processExpansionTree(processedTree, newDocument.categoryId, true, expansionMode, newDocument.categoryId);
                    }

                    // Open the new document (same pattern as SuggestTreeDialog)
                    if (onOpenDocument) {
                        try {
                            if (newDocument.categoryId) {
                                const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId} });
                                newDocument.subcases = response.data;
                            }
                            await onOpenDocument(newDocument);
                        } catch (error) {
                            console.error('Error opening new document:', error);
                        }
                    }
                } else {
                    console.error('New document missing categoryId:', newDocument);
                }
            } else {
                // Apply expansion to existing document
                if (!currentDocument) return;

                if (expansionMode === 'content-in-category') {
                    // First, create all categories without content
                    if (expansionResult?.placement && expansionResult.placement.parentId) {
                        await processExpansionTree(processedTree, expansionResult.placement.parentId, true, 'categories-only', expansionResult.placement.parentId);
                    } else {
                        const rootCategory = existingCategories[0];
                        if (!rootCategory) {
                            throw new Error('No root category found for document');
                        }
                        await processExpansionTree(processedTree, rootCategory.id, true, 'categories-only', rootCategory.id);
                    }
                    
                    // Then collect all content nodes and process them into the content category
                    const allContentNodes = collectAllContentNodes(processedTree);
                    if (allContentNodes.length > 0) {
                        let rootCategoryId: string;
                        if (expansionResult?.placement && expansionResult.placement.parentId) {
                            rootCategoryId = expansionResult.placement.parentId;
                        } else {
                            rootCategoryId = existingCategories[0].id;
                        }
                        const contentCategoryId = await getOrCreateContentCategory(rootCategoryId, currentDocument.name);
                        await processAllContentIntoCategory(allContentNodes, contentCategoryId);
                    }
                } else {
                    // Normal processing for other modes
                    if (expansionResult?.placement && expansionResult.placement.parentId) {
                        await processExpansionTree(processedTree, expansionResult.placement.parentId, true, expansionMode, expansionResult.placement.parentId);
                    } else {
                        const rootCategory = existingCategories[0];
                        if (!rootCategory) {
                            throw new Error('No root category found for document');
                        }
                        await processExpansionTree(processedTree, rootCategory.id, true, expansionMode, rootCategory.id);
                    }
                }

                // Add a small delay to ensure all backend operations are complete before opening
                await new Promise(resolve => setTimeout(resolve, 500));

                if (currentDocument.hasOpenTab && refreshCurrentDocument) {
                    // Document is currently open - refresh it
                    await refreshCurrentDocument();
                } else if (onOpenDocument && currentDocument) {
                    // Document is closed - open it automatically after expansion (same as new document creation)
                    try {
                        onOpenDocument(currentDocument);
                    } catch (error) {
                        console.error('Error opening closed document after expansion:', error);
                        alert(`Error opening document: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            }

            // Close the dialog after all operations are complete
            // Longer delay to ensure document opening completes
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error: any) {
            console.error('Error applying expansion:', error);
            alert(`Error applying expansion: ${error.message}`);
        } finally {
            setApplying(false);
        }
    };

    // Handle close
    const handleClose = () => {
        setShowContent(true);
        setSubcaseName('Expanded');
        setMergingOccurred(false);
        setProcessedTree(null);
        onClose();
    };

    // Determine which tree to render
    const treeToRender = processedTree || (expansionResult?.tree || null);



    // Render tree structure with new category distinction
    const renderTreeStructure = (node: any, level: number = 0, expansionResult: any = null, showContent: boolean = false): React.ReactNode => {
        if (!node) return null;

        const currentPath = `${node.name || node.text || ''}-${level}`;
        const padding = isRTL(node) ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            const children = Array.isArray(node.children)
                ? node.children.flatMap((child: any, idx: number) => renderTreeStructure(child, level + 1, expansionResult, showContent))
                : [];
            const isContentCat = isContentCategory(node);
            const isNew = isNewCategorySimple(node);

            // Choose colors based on category type and whether it's new
            // Priority: New categories > Content categories > Regular categories
            let iconColor = '#1976d2'; // Default blue for regular categories
            let textColor = 'inherit';

            if (isNew) {
                // New categories get highest priority - green color
                iconColor = '#4CAF50'; // Green for new categories
                textColor = '#2E7D32'; // Darker green for text
            } else if (isContentCat) {
                // Content categories (but not new) get light blue
                iconColor = '#87CEEB'; // Light blue for content categories
            }
            // Regular categories keep the default blue color

            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        {isContentCat ? (
                            <DescriptionIcon sx={{ color: iconColor, mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0 }} />
                        ) : (
                            <FolderIcon sx={{ color: iconColor, mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0 }} />
                        )}
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 700,
                                wordBreak: 'break-word',
                                textAlign: isRTL(node) ? 'right' : 'left',
                                flex: 1,
                                color: textColor
                            }}
                            dir="auto"
                        >
                            {node.name}
                            {isNew && (
                                <Box component="span" sx={{
                                    ml: 1,
                                    fontSize: '0.75rem',
                                    color: '#4CAF50',
                                    fontStyle: 'italic'
                                }}>
                                    (new)
                                </Box>
                            )}
                        </Typography>
                    </Box>
                    {/* Always render children - content categories will show their content inline */}
                    {children}
                </Box>
            );
        } else if (showContent && node.type === 'content' && node.text) {
            // Content node with text property (from hierarchical clustering)
            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        <DescriptionIcon sx={{ color: '#ff9800', mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0, fontSize: 20, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL(node) ? 'right' : 'left', flex: 1 }} dir="auto">
                            {node.text.substring(0, 150)}
                            {node.text.length > 150 ? '...' : ''}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        // Content node with index property (from content-based clustering)
        if (node.type === 'content' && node.index !== undefined) {
            // Try to get the original content if available
            let contentText = `Content ${node.index + 1}`;

            if (expansionResult?.originalContents && expansionResult.originalContents[node.index]) {
                const originalContent = expansionResult.originalContents[node.index];
                contentText = originalContent.text || contentText;
            }

            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL(node) ? 'right' : 'left', flex: 1 }} dir="auto">
                            {contentText.substring(0, 150)}
                            {contentText.length > 150 ? '...' : ''}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        return null;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Expand Tree Results
                {mergingOccurred && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        Note: Duplicate categories were merged automatically
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    {applying && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: 'rgba(255,255,255,0.9)',
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <CircularProgress size={40} />
                            <Typography variant="h6" sx={{ mt: 2, color: '#1976d2' }}>
                                Applying Expansion...
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                                Creating categories and content...
                            </Typography>
                        </Box>
                    )}
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Expansion Configuration
                        </Typography>
                    {showContent && (
                        <Tooltip title={getTooltipTextForState('suggest-tree-subcase-name', true)} placement="top">
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    label="Subcase Name"
                                    value={subcaseName}
                                    onChange={(e) => setSubcaseName(e.target.value)}
                                    size="small"
                                    fullWidth
                                    helperText="Enter the subcase name for expanded content"
                                    placeholder="e.g., Expanded, New Content, Additional"
                                />
                            </Box>
                        </Tooltip>
                    )}
                </Box>

                {/* Show Content Toggle */}
                <Box sx={{ mb: 2 }}>
                    <Tooltip title={getTooltipTextForState('suggest-tree-show-content', true)} placement="top">
                        <FormControlLabel
                            control={<Checkbox checked={showContent} onChange={e => setShowContent(e.target.checked)} />}
                            label="Show content text"
                        />
                    </Tooltip>


                </Box>

                {/* Unified Tree Structure */}
                {treeToRender && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Complete Document Structure (Including New Content)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{
                                maxHeight: 400,
                                overflow: 'auto',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                p: 2
                            }}>
                                {renderTreeStructure(treeToRender, 0, expansionResult, showContent)}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Expansion Options */}
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        <strong>Expansion Options:</strong>
                    </Typography>
                    
                    {/* Expansion Mode Selection */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Choose how to handle content:
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={expansionMode === 'with-content'}
                                    onChange={() => setExpansionMode('with-content')}
                                />
                            }
                            label="Create tree with content (default)"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={expansionMode === 'content-in-category'}
                                    onChange={() => setExpansionMode('content-in-category')}
                                />
                            }
                            label="Create categories and save all content in one 'Content of [Document Name]' category under the root"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={expansionMode === 'categories-only'}
                                    onChange={() => setExpansionMode('categories-only')}
                                />
                            }
                            label="Create categories only (discard content)"
                        />
                    </Box>
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={createNewDocument}
                                onChange={(e) => handleCreateNewDocumentChange(e.target.checked)}
                            />
                        }
                        label="Create new document with expanded tree"
                    />
                    {createNewDocument && (
                        <Box sx={{ mt: 1, ml: 4 }}>
                            <TextField
                                label="New Document Name"
                                value={newDocumentName}
                                onChange={(e) => setNewDocumentName(e.target.value)}
                                placeholder="Enter document name"
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                This will create a new document with the expanded tree structure
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'text.secondary' }}>
                    <strong>Note:</strong> The tree structure above shows:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        • 📁 Categories (blue folder icon) - can contain subcategories
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        • 📄 Content categories (light blue document icon) - contain only content items
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        • 🆕 New categories (green icon) - will be created when applied
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        Review the expanded structure above. When you're ready, click "Apply Expansion" to:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        • <strong>With Content:</strong> Integrate new content into existing categories and create new categories with content
                    </Typography>
                    <Typography variant="body2" component="div">
                        • <strong>Content in Category:</strong> Create categories and collect all content in one "Content of [Document Name]" category under the root
                    </Typography>
                    <Typography variant="body2" component="div">
                        • <strong>Categories Only:</strong> Create the category structure without any content items
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        • Update your document structure based on the selected mode
                    </Typography>
                </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={applying}>
                    Cancel
                </Button>
                <Button
                    onClick={handleApplyExpansion}
                    variant="contained"
                    disabled={applying || !currentDocument || !currentDocument.id || fetchingCategories}
                    startIcon={<AddIcon />}
                >
                    Apply Expansion
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpandTreeResultsDialog; 


